<template>
  <div>
    <VTextField type="text" id="autocomplete" :modelValue="modelValue" @input="handleInput" autocomplete="off" placeholder="Enter address" aria-label="autofill address" appearance="ghost" class="vivid-input"></VTextField>
    <div v-if="loading" class="loading-indicator">
      <VProgressRing :size="-5" />
      <span>Searching addresses...</span>
    </div>
    <ul v-if="suggestions.length && !loading" class="suggestions-list">
      <li v-for="(item, index) in suggestions" :key="index" @click="selectSuggestion(item)"  class="searchOptions"> {{ item.description }} </li>
    </ul>
  </div>
</template>

<script>
import apiInterceptor from '@/shared/apiInterceptor';
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env.js';

export default {
  emits: ['update:modelValue', 'onSearch'],
  props: {
    modelValue: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      suggestions: [],
      typingTimeout: null,
      loading: false
    };
  },
  methods: {
    handleInput(event) {
      const val = typeof event === 'string' ? event : (event?.target?.value ?? '');
      this.$emit('update:modelValue', val);

      clearTimeout(this.typingTimeout);
      if (val && val.length >= 3) {
        this.typingTimeout = setTimeout(() => {
          this.getAutofillLocation(val);
        }, 500);
      } else {
        this.suggestions = [];
      }
    },

    async getAutofillLocation(address) {
      try {
        this.loading = true;
        const params = { address };
        const response = await apiInterceptor.get(
          environment("VUE_APP_API_URL") + "shared-services/v1/utils/gmaps/autofill",
          { params }
        );
        this.suggestions = response || [];
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong, please try again",
          type: "danger",
        });
      } finally {
        this.loading = false;
      }
    },
    async selectSuggestion(item) {
      this.suggestions = [];
      try {
        const locationCoordinates = await this.getLocationCoordinates(item.place_id);
        const locationData = {
          autoFillAddress: item.description,
          sendLocationLatitudeText: locationCoordinates.lat,
          sendLocationLongitudeText: locationCoordinates.lng,
        };
        this.$emit('update:modelValue', item.description);
        this.$emit('onSearch', locationData);
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong while selecting location",
          type: "danger",
        });
      }
    },
    async getLocationCoordinates(placeId) {
      try {
        useUIStore().updateLoading({ isLoading: true, message: 'Fetching location coordinates...' });
        const response = await apiInterceptor.get(
          environment("VUE_APP_API_URL") + "shared-services/v1/utils/gmaps/coordinates",
          { params: { place_id: placeId } }
        );
        return response?.location;
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Failed to fetch location coordinates",
          type: "danger",
        });
        throw error;
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    }
  }
};
</script>

<style scoped lang="scss">

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background-color: var(--white);
  border: 1px solid var(--background-primary);
  border-radius: 5px;
  margin-top: 2px;
  font-size: 13px;
  color: var(--gray-04);
}

.suggestions-list {
  list-style: none;
  margin: 0;
  padding: 0;
  background-color: var(--white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  overflow: hidden;
  z-index: 10;
  border: 1px solid var(--background-primary);
  margin-top: 2px; 
  left: 0;
  border-radius: 5px;
  max-height: 200px;
  overflow-y: auto;

  li {
    padding: 10px 12px;
    border-bottom: 1px solid var(--background-primary);
    color: var(--black);
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: var(--purple-opac);
      color: var(--purple);
    }
  }
}
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
</style>
